/**
 * Field definitions for change tracking
 * Defines which fields to track for each entity type
 */

export const TRACKABLE_FIELDS = {
  // Core entities
  channels: [
    'name',
    'description',
    'price',
    'category_id',
    'is_local',
    'is_duplicate',
    'linked_provider_id',
  ],

  plans: [
    'name',
    'monthly_price',
    'yearly_price',
    'color_index',
    'features',
    'description',
    'is_addon',
  ],

  categories: [
    'name',
    'parent_id',
    'display_order',
    'path',
    'price',
    'description',
    'is_addon',
  ],

  packages: [
    'name',
    'description',
    'monthly_price',
    'yearly_price',
    'package_type',
    'is_addon',
    'is_featured',
    'display_order',
    'color_index',
    'features',
    'marketing_tagline',
    'best_for',
  ],

  channel_types: [
    'name',
    'display_order',
    'is_default',
    'is_sequence',
  ],

  // Relationship tables (track as separate entities)
  channel_plans: ['channel_id', 'plan_id'],
  channel_categories: ['channel_id', 'category_id', 'is_default'],
  channel_numbers: ['channel_id', 'type_id', 'number', 'is_sequence', 'location_id'],
  channel_tag_assignments: ['channel_id', 'tag_id'],
  package_plans: ['package_id', 'plan_id', 'display_order'],
  package_categories: ['package_id', 'category_id', 'display_order'],
  package_channels: ['package_id', 'channel_id', 'display_order'],

  // Tags
  channel_tags: ['name', 'description'],

  // Provider features (7 features)
  additional_fees: [
    'entity_type',
    'entity_id',
    'fee_type',
    'amount',
    'description',
    'is_recurring',
    'is_required',
    'custom_fee_type_name',
    'display_order',
    'internal_reference',
  ],

  promotional_offers: [
    'entity_type',
    'entity_id',
    'title',
    'description',
    'original_price',
    'promotional_price',
    'discount_percentage',
    'starts_at',
    'ends_at',
    'is_new_customer_only',
    'terms_conditions',
    'discount_type',
    'eligibility_requirements',
    'is_recurring',
    'status',
    'custom_discount_type_name',
    'display_order',
    'internal_reference',
  ],

  streaming_specifications: [
    'entity_type',
    'entity_id',
    'simultaneous_streams',
    'offline_downloads',
    'max_video_quality',
    'download_limit_days',
    'hdr_support',
    'dolby_atmos_support',
  ],

  service_terms: [
    'entity_type',
    'entity_id',
    'contract_required',
    'cancellation_policy',
    'installation_required',
    'price_lock_period_months',
  ],

  content_coverage: [
    'sports_coverage',
    'news_coverage',
    'international_content',
    'kids_family_content',
    'premium_movies',
    'local_channels',
    'original_content',
  ],

  service_quality: [
    'customer_support_hours',
    'support_channels',
    'setup_difficulty',
    'reliability_rating',
    'customer_satisfaction_score',
  ],

  provider_device_support: [
    'device_id',
    'is_supported',
    'notes',
    'entity_type',
    'entity_id',
  ],

  // Provider metadata
  providers: [
    'name',
    'logo_url',
    'support_email',
    'website_url',
    'support_phone',
    'free_trial_days',
    'student_discount_available',
    'currency',
    'has_sequence_numbers',
  ],

  // Region metadata
  regions: [
    'name',
    'zip_code',
    'country_code',
    'state_province',
    'city_coverage',
    'rural_availability',
  ],
} as const;

export type EntityType = keyof typeof TRACKABLE_FIELDS;

/**
 * Get trackable fields for an entity type
 */
export function getTrackableFields(entityType: EntityType): readonly string[] {
  return TRACKABLE_FIELDS[entityType] || [];
}